import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { keys } from '../services/queryKeys'
import { getPartners } from '../services/partnerService'
import PartnerRow from '../components/partners/PartnerRow'
import { RowSkeleton, Empty, Failed } from '../components/common/States'

const firstNameOf = (displayName) => String(displayName || '').trim().split(' ')[0]

export default function Home() {
  const { user } = useAuth()
  const [search, setSearch] = useState('')

  // Keyed by the search term, so typing back to a previous term is instant and going
  // Home → a partner → back costs nothing inside the stale window.
  const { isPending: loading, data, error } = useQuery({
    queryKey: keys.partners.list({ search }),
    queryFn: () => getPartners({ search }),
  })
  const partners = data?.data ?? []
  const total = data?.pagination?.total ?? 0
  const first = firstNameOf(user?.displayName)

  return (
    <section>
      <div className="px-5 pt-[18px] pb-3">
        {/* The service area, not a guess at where the customer is standing. */}
        <p className="text-label font-semibold uppercase text-ink-3">Laguna</p>
        <h1 className="mt-1.5 font-display font-bold text-display-l text-ink">
          {first ? `Who rides for you, ${first}?` : 'Who rides for you?'}
        </h1>
      </div>

      {/* Sticky, because it is the only tool on the screen. It parks under the
          desktop header, and at the top of the viewport where there is none. */}
      <div className="sticky top-0 md:top-16 z-20 px-5 pt-2 pb-3 bg-shell border-b border-hairline">
        <div className="relative flex items-center">
          <label htmlFor="partner-search" className="sr-only">
            Search delivery services
          </label>

          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="absolute left-3 w-4 h-4 text-ink-3 pointer-events-none"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>

          <input
            id="partner-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search a name, a town, or what you need moved"
            className="w-full min-h-[44px] bg-surface border border-edge rounded-chip
                       pl-9 pr-3.5 py-2.5 text-body text-ink placeholder:text-ink-3
                       outline-none transition-colors duration-quick ease-out
                       focus:border-brand focus:shadow-[0_0_0_3px_var(--blush)]"
          />
        </div>
      </div>

      {loading && (
        <div className="px-5 pt-4">
          <RowSkeleton count={5} />
        </div>
      )}

      {!loading && error && <Failed error={error} />}

      {!loading && !error && partners.length === 0 && (
        <Empty
          title={search ? 'No service matched' : 'No delivery services yet'}
          note={
            search
              ? 'Try a town, a barangay, or the thing you need fetched.'
              : 'Once a partner joins, they will show up here.'
          }
        />
      )}

      {!loading && !error && partners.length > 0 && (
        <>
          <p className="px-5 pt-3.5 pb-1.5 text-label font-semibold uppercase text-ink-3">
            {total} {total === 1 ? 'service' : 'services'} open now
          </p>

          <ul className="px-5 pb-6">
            {partners.map((partner) => (
              <li key={partner.uid}>
                <PartnerRow partner={partner} />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
